// src/components/black-friday/chart-modal.tsx
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { BFDailyCampaignData } from "@/types/black-friday";

interface ChartDataPoint {
  index: number;
  [key: string]: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: ChartDataPoint[];
  format?: "number" | "currency" | "percentage";
}

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const daysText = label > 0 ? `BF +${label}` : label === 0 ? "Black Friday" : `BF ${label}`;

    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold mb-2">{daysText}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function ChartModal({ isOpen, onClose, title, data, format = "number" }: Props) {
  const formatYAxis = (value: number) => {
    if (format === "currency") {
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
      return `$${value.toFixed(0)}`;
    }
    if (format === "percentage") {
      return `${value.toFixed(1)}%`;
    }
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 w-full h-full pb-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, bottom: 30, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="index"
                label={{ value: "Days from Black Friday", position: "insideBottom", offset: -10 }}
                className="text-sm"
              />
              <YAxis tickFormatter={formatYAxis} className="text-sm" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <ReferenceLine
                x={0}
                stroke="hsl(var(--destructive))"
                strokeDasharray="3 3"
                label={{ value: "Black Friday", position: "top" }}
              />
              <Line
                type="monotone"
                dataKey="year2025"
                stroke="#3b82f6"
                strokeWidth={3}
                name="2025"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="year2024"
                stroke="#f97316"
                strokeWidth={3}
                name="2024"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="year2023"
                stroke="#10b981"
                strokeWidth={3}
                name="2023"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
}
